import type { AuthTokens, User } from '@/shared/types/auth.types'
import { AUTH_SESSION_KEYS, AUTH_STORAGE_KEYS } from '@/shared/constants/authStorageConstants'

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function isValidToken(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim() !== '' &&
    value !== 'undefined' &&
    value !== 'null'
  )
}

export function getSafeRedirectPath(candidate: string | null | undefined, fallback = '/'): string {
  if (!candidate || typeof candidate !== 'string') {
    return fallback
  }

  if (!candidate.startsWith('/') || candidate.startsWith('//')) {
    return fallback
  }

  return candidate
}

export function setPostLoginRedirect(path: string | null | undefined): void {
  if (!isBrowser()) {
    return
  }

  sessionStorage.setItem(
    AUTH_SESSION_KEYS.postLoginRedirect,
    getSafeRedirectPath(path),
  )
}

export function consumePostLoginRedirect(fallback = '/'): string {
  if (!isBrowser()) {
    return fallback
  }

  const stored = sessionStorage.getItem(AUTH_SESSION_KEYS.postLoginRedirect)
  sessionStorage.removeItem(AUTH_SESSION_KEYS.postLoginRedirect)
  return getSafeRedirectPath(stored, fallback)
}

export function setStoredTokens(tokens: AuthTokens): void {
  if (!isBrowser()) {
    return
  }

  localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.accessToken)
}

export function clearStoredTokens(): void {
  if (!isBrowser()) {
    return
  }

  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken)
}

export function getStoredTokens(): AuthTokens | null {
  if (!isBrowser()) {
    return null
  }

  const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken)

  if (!isValidToken(accessToken)) {
    return null
  }

  return { accessToken }
}

export function setStoredUser(user: User): void {
  if (!isBrowser()) {
    return
  }

  localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user))
}

export function clearStoredUser(): void {
  if (!isBrowser()) {
    return
  }

  localStorage.removeItem(AUTH_STORAGE_KEYS.user)
}

export function getStoredUser(): User | null {
  if (!isBrowser()) {
    return null
  }

  const stored = localStorage.getItem(AUTH_STORAGE_KEYS.user)
  if (!stored) {
    return null
  }

  try {
    return JSON.parse(stored) as User
  } catch {
    return null
  }
}
